import React from 'react';
import { Pressable, Text, View, StyleSheet } from 'react-native';
import { Ionicons, Feather } from '@expo/vector-icons';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { formatRating } from '../../../../utils/formatNumber';
import { usePlannedMealTitle } from '../plannedMealTitle';
import { Paprika } from '../../../../styles/theme';
import type { RecipeIndexEntry } from '../../../../services/unifiedRecipeService';
import type { PlannedMeal } from '../../../../redux/mealPlanSlice';
import { acceptMeal } from '../../../../redux/mealPlanSlice';
import { ApprovalThumbs } from '../../ApprovalThumbs';
import { VerdictAvatars } from '../../../household/VerdictAvatars';
import { useHouseholdMembers } from '../../../../hooks/useHouseholdMembers';
import { canAcceptProposal, canRemoveProposal } from '../../proposalState';
import { nameForViewer } from '../../../../services/householdService';
import { RemoteImage } from '../../../shared/RemoteImage';

interface Props {
  recipe: RecipeIndexEntry;
  meal: PlannedMeal;
  isCooked?: boolean;
  /** Opens the recipe detail drawer for this dish. */
  onPress: () => void;
  /** Removes this meal from the slot. */
  onRemove?: () => void;
}

function formatMinutes(m: number): string {
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h${m % 60 ? String(m % 60).padStart(2, '0') : ''}`;
}

/**
 * The 2nd/3rd dish of a slot on desktop: the same card language as SlotCard —
 * photo, title, rating and time — at a size that fits under it in a grid cell.
 *
 * When the meal is a proposal (mode === 'proposed'), shows a special treatment:
 * dashed border, author tag, approval thumbs, and a "Put on menu" button.
 * Gated on status === 'paired' (solo users don't see proposals).
 */
export function SlotExtraCard({ recipe, meal, isCooked, onPress, onRemove }: Props) {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const title = usePlannedMealTitle(recipe, 'desktop-extra');
  const { status, myUid, roster } = useHouseholdMembers();
  const hasRating = recipe.sourceRating != null && recipe.sourceRating > 0;
  const hasTime = recipe.readyInMinutes != null && recipe.readyInMinutes > 0;

  const isProposal = meal.mode === 'proposed' && status === 'paired';
  const canAccept = isProposal && canAcceptProposal({ myUid, cookId: meal.cookId });
  const canRemove = isProposal && canRemoveProposal({ proposal: meal, myUid, cookId: meal.cookId });

  const handleAccept = (e: any) => {
    e.stopPropagation?.();
    if (!myUid) return;
    dispatch(acceptMeal({ mealId: meal.id, uid: myUid }));
  };

  const handleRemove = (e: any) => {
    e.stopPropagation?.();
    if (onRemove) {
      onRemove();
    }
  };

  if (isProposal) {
    return (
      <Pressable style={[styles.card, styles.proposalCard]} onPress={onPress}>
        <RemoteImage
          uri={recipe.image}
          variant="thumb"
          style={styles.thumb}
          resizeMode="cover"
          fallback={<View style={[styles.thumb, styles.thumbPlaceholder]} />}
        />

        <View style={styles.body}>
          <Text style={styles.proposalAuthor}>
            {t('household.approval.proposedBy', {
              name: meal.createdByUid
                ? nameForViewer(meal.createdByUid, myUid, roster, t('household.approval.self'))
                : '?',
            })}
          </Text>
          <Text style={styles.title} numberOfLines={2} testID="planned-meal-title">{title}</Text>
          {(hasRating || hasTime || isCooked) && (
            <View style={styles.meta}>
              {hasRating && (
                <>
                  <Ionicons name="star" size={10} color="#F5A623" />
                  <Text style={styles.metaText}>{formatRating(recipe.sourceRating!, i18n.language)}</Text>
                </>
              )}
              {hasTime && (
                <Text style={styles.metaText}>
                  {hasRating ? '· ' : ''}{formatMinutes(recipe.readyInMinutes!)}
                </Text>
              )}
              {isCooked && <Text style={styles.cooked}>{t('thisWeek.cooked')}</Text>}
              {isCooked && <VerdictAvatars recipeId={recipe.id} variant="paperDense" />}
            </View>
          )}
        </View>

        <View style={styles.proposalControls}>
          <View style={styles.proposalButtons}>
            {canAccept && (
              <Pressable style={styles.putOnMenuBtn} onPress={handleAccept} hitSlop={8}>
                <Text style={styles.putOnMenuText}>{t('household.approval.putOnMenu')}</Text>
              </Pressable>
            )}
            {canRemove && (
              <Pressable style={styles.removeBtn} onPress={handleRemove} hitSlop={8}>
                <Feather name="x" size={16} color={Paprika.textMuted} />
              </Pressable>
            )}
          </View>
          <ApprovalThumbs mealId={meal.id} variant="outline" />
        </View>
      </Pressable>
    );
  }

  return (
    <Pressable style={styles.card} onPress={onPress}>
      <RemoteImage
        uri={recipe.image}
        variant="thumb"
        style={styles.thumb}
        resizeMode="cover"
        fallback={<View style={[styles.thumb, styles.thumbPlaceholder]} />}
      />

      <View style={styles.body}>
        <Text style={styles.title} numberOfLines={2} testID="planned-meal-title">{title}</Text>
        {(hasRating || hasTime || isCooked) && (
          <View style={styles.meta}>
            {hasRating && (
              <>
                <Ionicons name="star" size={10} color="#F5A623" />
                <Text style={styles.metaText}>{formatRating(recipe.sourceRating!, i18n.language)}</Text>
              </>
            )}
            {hasTime && (
              <Text style={styles.metaText}>
                {hasRating ? '· ' : ''}{formatMinutes(recipe.readyInMinutes!)}
              </Text>
            )}
            {isCooked && <Text style={styles.cooked}>{t('thisWeek.cooked')}</Text>}
            {isCooked && <VerdictAvatars recipeId={recipe.id} variant="paperDense" />}
          </View>
        )}
      </View>

      {/* Show thumbs on every accepted recipe meal when paired */}
      {status === 'paired' && (
        <View style={styles.approvalRow}>
          <ApprovalThumbs mealId={meal.id} variant="outline" />
        </View>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 6,
    borderRadius: 10,
    backgroundColor: Paprika.surfaceLowest,
    shadowColor: 'rgba(82, 68, 60, 0.08)',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 1,
    shadowRadius: 5,
    elevation: 1,
  },
  proposalCard: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 6,
    padding: 8,
    backgroundColor: Paprika.approvalIdeaTint,
    borderWidth: 1,
    borderStyle: 'dashed' as any,
    borderColor: Paprika.approvalIdea,
    shadowColor: 'rgba(255, 255, 255, 0)',
    shadowOpacity: 0,
    elevation: 0,
  },
  proposalAuthor: {
    fontFamily: 'Inter-Medium',
    fontSize: 9,
    color: Paprika.approvalIdea,
    textTransform: 'uppercase' as any,
    letterSpacing: 0.5,
  },
  // Wraps because a grid cell is one seventh of the window: at 1440px that is
  // ~143px of content, and "Mettre au menu" plus the dismiss plus two 32px
  // thumbs do not fit on one line. Without this the thumbs render 30px outside
  // the cell, over the next day's column.
  proposalControls: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    columnGap: 8,
    rowGap: 6,
  },
  proposalButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  putOnMenuBtn: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
    backgroundColor: Paprika.approvalIdea,
  },
  putOnMenuText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 9,
    color: Paprika.surfaceLowest,
    textTransform: 'uppercase' as any,
    letterSpacing: 0.3,
  },
  removeBtn: {
    padding: 4,
  },
  thumb: {
    width: 40,
    height: 40,
    borderRadius: 8,
    flexShrink: 0,
    backgroundColor: Paprika.surfaceLow,
  },
  thumbPlaceholder: {
    backgroundColor: Paprika.bgSunk,
  },
  body: {
    flex: 1,
    minWidth: 0,
    gap: 3,
  },
  title: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 12,
    lineHeight: 15,
    color: Paprika.textPrimary,
  },
  meta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexWrap: 'wrap',
  },
  metaText: {
    fontFamily: 'Inter-Medium',
    fontSize: 10.5,
    color: Paprika.textSecondary,
  },
  cooked: {
    fontFamily: 'Inter-Medium',
    fontSize: 10.5,
    color: Paprika.primary,
  },
  approvalRow: {
    marginLeft: 'auto' as any,
  },
});

export default SlotExtraCard;
